import { isSupportedRegion, normalizeCountry } from './elementFormat/phone';

/**
 * Country (region) vocabulary for the `phone` element's `country` option —
 * the region a national number is parsed against and read back in.
 *
 * WHY A CURATED LIST AND NOT EVERY REGION. libphonenumber knows some 250 of
 * them, and the one module allowed to require it is the vendored
 * utils/elementFormat/phone.js, which deliberately exports no roster (there
 * must be exactly one call site, so the two repos cannot drift on what parses).
 * So the picker offers a short list of the regions a card author is actually
 * likely to want, and anything else is TYPED: the search box accepts a
 * two-letter code and offers it the moment libphonenumber confirms it is real
 * (`isSupportedRegion`). Nothing is lost — every region remains selectable —
 * and no second country table exists to fall out of step with the first.
 *
 * The element's CURRENT value is always offered, listed or not, so opening a
 * card set to a region this list never mentions does not silently reset it.
 */

// Roughly: the anglophone world, the EU's larger members, and the largest
// economies elsewhere. Order is intentional — the list is short enough to
// scan, and the search box is there the moment it is not.
export const COMMON_COUNTRIES = [
	'US', 'CA', 'GB', 'IE', 'AU', 'NZ',
	'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'CH', 'AT', 'PT', 'GR',
	'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'RO', 'UA',
	'MX', 'BR', 'AR', 'CL', 'CO',
	'JP', 'KR', 'CN', 'HK', 'TW', 'SG', 'MY', 'TH', 'VN', 'PH', 'ID', 'IN', 'PK',
	'ZA', 'NG', 'KE', 'EG', 'MA',
	'AE', 'SA', 'IL', 'TR', 'RU',
];

/**
 * A region code as a word a person recognises: "United States", not "US".
 * `Intl.DisplayNames` ships with every browser the app supports and with
 * Node's full-icu build, but it is asked for inside a try — a runtime without
 * the region data must fall back to the code rather than take a form down.
 */
export function countryName(code) {
	const region = normalizeCountry(code) || String(code || '').toUpperCase();
	try {
		const names = new Intl.DisplayNames(undefined, { type: 'region' });
		return names.of(region) || region;
	} catch (e) {
		return region;
	}
}

/** "United States (US)" — the name to read, the code that is stored. */
export const countryLabel = code => `${countryName(code)} (${code})`;

/**
 * The Select's options: the curated list, plus the element's current value
 * and plus whatever real region the author has typed into the search box,
 * each at the top where a just-typed thing belongs.
 */
export function countryOptions({ value, search } = {}) {
	const codes = [...COMMON_COUNTRIES];
	const prepend = code => {
		const region = normalizeCountry(code);
		if (region && !codes.includes(region)) codes.unshift(region);
	};
	prepend(value);
	if (search && isSupportedRegion(search)) prepend(search);
	return codes.map(code => ({ value: code, label: countryLabel(code) }));
}
